import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, ClipboardList, Gauge, Hash, Package, PackagePlus, RotateCcw, ShieldCheck, Store, Tags, Undo2, Unplug, Wrench, X } from 'lucide-react';
import '../../equipe2/components/TechnicianModal.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { Ci2HelpHexIcon } from './Ci2HelpHexIcon';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import {
  CAMPOS_CONFIG,
  COND_TIPO_OS_FIELDS,
  formatSemPendenciaTipoOsBlockMessage,
  getSemPendenciaTipoOsIssues,
  isExactAlnumCode,
  labelRegBugOption,
  normCondOsFlag,
  normalizeStockValidationScope,
  sanitizeAlnumCode,
  sanitizeMotivoDraft,
  STOCK_VALIDATION_OPTIONS,
  TOOLTIP_ATUALIZA_SLA,
  TOOLTIP_DESINSTALA_EQUIP,
  TOOLTIP_INSTALA_EQUIP,
  TOOLTIP_NAO_LIGA,
  TOOLTIP_SEM_PENDENCIA,
  TOOLTIP_TIPO_OS,
} from './condicoesIntervShared';

const GRUPO = ['operacao', 'condicao', 'status_disponivel', 'status_indisponivel'];
const TIPO_ICONS = {
  allow_corrective: Wrench,
  allow_install: PackagePlus,
  allow_desinstall: Unplug,
  allow_preventive: ShieldCheck,
  allow_stock: Package,
  allow_recharge: RotateCcw,
  allow_measurement: Gauge,
  allow_counter: Store,
};
const TABS = [
  { id: 'geral', label: 'Geral', Icon: Tags, desc: 'Identificação, tipos de OS e funções da condição.' },
  { id: 'pedido', label: 'Dados para pedido', Icon: ClipboardList, desc: 'Operação, condição e status usados no pedido.' },
  { id: 'retorno', label: 'Dados para retorno', Icon: Undo2, desc: 'Status aplicado no retorno técnico.' },
  { id: 'estoque', label: 'Liberação de OS por estoque', Icon: Package, desc: 'Status do pedido de peça quando a saída de estoque está liberada.' },
];
const PEDIDO_KEYS = ['operacao', 'condicao', 'status_disponivel', 'status_indisponivel'];
const RETORNO_HELP = 'Defina o código de status aplicado ao retorno técnico.';
const SERVICO_HELPS = {
  servico: 'Serviço que a OS de retorno já traz selecionado.',
  motivo: 'Texto que já vem escrito no motivo da nova OS.',
  blank: 'A OS de retorno abre sem um serviço marcado.',
  ask: 'No fechamento, o novo ciclo só segue depois que alguém escolhe o serviço.',
};
const FLAGS = [
  { state: 'update_sla', label: 'Atualiza SLA', path: 'update-sla', body: 'updateSla', help: TOOLTIP_ATUALIZA_SLA },
  { state: 'func_install', label: 'Instala equipamento', path: 'install', body: 'install', other: 'hasOtherInstalaEquip', otherMsg: 'Já existe uma condição com "Instala equipamento". Desative-a antes.', help: TOOLTIP_INSTALA_EQUIP },
  { state: 'func_uninstall', label: 'Desinstala equipamento', path: 'uninstall', body: 'uninstall', other: 'hasOtherDesinstalaEquip', otherMsg: 'Já existe uma condição com "Desinstala equipamento". Desative-a antes.', help: TOOLTIP_DESINSTALA_EQUIP },
  { state: 'func_counters', label: 'Notifica contadores', path: 'counters', body: 'notify' },
  { state: 'func_nao_liga', label: 'Não liga', path: 'nao-liga', body: 'naoLiga', other: 'hasOtherNaoLiga', otherMsg: 'Já existe uma condição com "Não liga". Desative-a antes.', help: TOOLTIP_NAO_LIGA },
  { state: 'func_sem_pendencia', label: 'Sem pendência', path: 'sem-pendencia', body: 'semPendencia', other: 'hasOtherSemPendencia', otherMsg: 'Já existe uma condição com "Sem pendência". Desative-a antes.', semPendencia: true, help: TOOLTIP_SEM_PENDENCIA },
];

function ServicoPicker({ value, items, disabled, onChange, onNeedItems }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const layout = useEcFilterPopoverLayout(anchorRef, open, 220, 280);
  const sel = String(value || '').trim();

  useEffect(() => { onNeedItems?.(); }, [onNeedItems]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-tm-cond-servico]');
      if (pop?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const options = useMemo(() => {
    const all = [
      { cod: '', label: '(Nenhum)' },
      ...items.map((b) => ({ cod: String(b.cod_bug || '').trim(), label: labelRegBugOption(b) })),
    ];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((o) => o.label.toLowerCase().includes(q) || o.cod.toLowerCase().includes(q));
  }, [items, search]);

  const current = items.find((b) => String(b.cod_bug || '').trim() === sel);
  const label = sel === '' ? '(Nenhum)' : (current ? labelRegBugOption(current) : sel);
  const popStyle = layout ? { ...buildEcFilterPopoverStyle(layout), zIndex: 1300 } : null;

  return (
    <>
      <div className={`tm-combo${open ? ' is-open' : ''}`} ref={anchorRef}>
        <button
          type="button"
          id="tm-cond-defeito"
          className="tm-control tm-combo-trigger"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Selecionar serviço"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="tm-combo-value">{label}</span>
          <ChevronDown size={14} strokeWidth={1.75} className="tm-combo-chevron" aria-hidden />
        </button>
      </div>
      {open && popStyle ? ReactDOM.createPortal(
        <div
          data-tm-cond-servico=""
          className={EC_FILTER_POPOVER_CLASS}
          style={popStyle}
          role="dialog"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <BoardSearchSimplePickerPanel
            searchPlaceholder="Buscar serviço..."
            searchTerm={search}
            onSearchChange={setSearch}
            items={options}
            getKey={(item) => item.cod || '__none'}
            getLabel={(item) => item.label}
            isSelected={(item) => item.cod === sel}
            onToggle={(item) => {
              onChange(item.cod);
              setSearch('');
              setOpen(false);
            }}
            emptyMessage="Nenhum serviço"
            listAriaLabel="Serviço"
          />
        </div>,
        document.body
      ) : null}
    </>
  );
}

const EMPTY = {
  name: '',
  erpCode: '',
  isActive: true,
  operacao: '',
  condicao: '',
  status_disponivel: '',
  status_indisponivel: '',
  status_retorno: '',
  status_peca_disp: '',
  stock_validation_scope: 'all_companies',
  codbug: '',
  codbugReason: '',
  codbugBlank: false,
  askServiceOnNewCycle: false,
  tipos: {},
  flags: {},
};

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function onFlag(row, key) {
  return row?.[key] === 1 || row?.[key] === true || String(row?.[key]) === '1';
}

function TmSwitch({ checked, onChange, disabled, id }) {
  return (
    <button type="button" id={id} role="switch" aria-checked={checked} disabled={disabled} className={`tm-switch${checked ? ' is-on' : ''}`} onClick={() => !disabled && onChange(!checked)}>
      <span className="tm-switch-thumb" />
    </button>
  );
}

async function patchJson(baseUrl, path, body) {
  const res = await fetch(`${baseUrl}/api/config/condicoes-intervencao/${path}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.message || 'Não foi possível salvar');
  return json;
}

export function CondicoesIntervNativeConditionModal({
  open,
  mode,
  codigo,
  row,
  api,
  nativeMode = false,
  baseUrl,
  erpTransition = false,
  onClose,
  onSaved,
}) {
  const isEdit = mode === 'edit';
  const enabled = !isEdit || row?.func_close === 1;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [createdCode, setCreatedCode] = useState(null);
  const [tab, setTab] = useState('geral');
  const [helpKey, setHelpKey] = useState(null);
  const [erpError, setErpError] = useState(null);
  const rowRef = useRef(row);
  rowRef.current = row;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !helpKey) return undefined;
    const onDoc = (e) => {
      if (e.target.closest?.('.tm-cond-help')) return;
      setHelpKey(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, helpKey]);

  useEffect(() => {
    if (!open) return undefined;
    const current = rowRef.current;
    setError(null);
    setErpError(null);
    setHelpKey(null);
    setTab('geral');
    setCreatedCode(null);
    if (!isEdit) {
      setForm(EMPTY);
      setLoading(false);
      return undefined;
    }
    if (!nativeMode) {
      setForm(formFromRow(current));
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setForm(formFromRow(current));
    fetch(`${baseUrl}/api/config/condicoes-intervencao/native/conditions/${encodeURIComponent(codigo)}`, { headers: authHeaders() })
      .then((res) => res.json().then((json) => ({ res, json })))
      .then(({ res, json }) => {
        if (cancelled) return;
        if (!res.ok || !json.success) throw new Error(json.message || 'Falha ao carregar a condição');
        setForm((prev) => ({
          ...prev,
          name: json.data.name || '',
          erpCode: json.data.erpCode || '',
          isActive: json.data.isActive !== false,
        }));
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Erro ao carregar dados'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, isEdit, nativeMode, codigo, baseUrl]);

  const tipoRow = useMemo(() => {
    if (isEdit && row) return row;
    const flags = {};
    COND_TIPO_OS_FIELDS.forEach((f) => { flags[f.db] = form.tipos[f.db] ? 1 : 0; });
    return { ...flags, func_sem_pendencia: form.flags.func_sem_pendencia ? 1 : 0, TB01055_CODIGO: codigo || 'nova' };
  }, [isEdit, row, form.tipos, form.flags, codigo]);

  const setCampo = (key, len) => (e) => {
    setForm((prev) => ({ ...prev, [key]: sanitizeAlnumCode(e.target.value, len) }));
  };

  const toggleTipo = (item) => {
    if (!enabled) return;
    if (isEdit && row) {
      const isOn = normCondOsFlag(row[item.db]) === 1;
      api?.onTipoOsToggle?.(row, item, !isOn);
      return;
    }
    setForm((prev) => ({ ...prev, tipos: { ...prev.tipos, [item.db]: !prev.tipos[item.db] } }));
  };

  const toggleFlag = async (flag, next) => {
    if (!enabled) return;
    if (!isEdit || !row) {
      setForm((prev) => ({ ...prev, flags: { ...prev.flags, [flag.state]: next } }));
      return;
    }
    const cod = row.TB01055_CODIGO;
    if (next && flag.other && api?.[flag.other]?.(cod)) {
      api.toastError?.(flag.otherMsg);
      return;
    }
    if (next && flag.semPendencia && api?.erpTransition && /^N\d+$/i.test(String(cod || ''))) {
      api.toastError?.('Enquanto houver OS do ERP em aberto nesta transição, "Sem pendência" só pode ficar numa condição com código do ERP.');
      return;
    }
    if (next && flag.semPendencia) {
      const block = formatSemPendenciaTipoOsBlockMessage(getSemPendenciaTipoOsIssues(row), { forEnable: true });
      if (block) {
        api.toastError?.(block);
        return;
      }
    }
    try {
      await patchJson(baseUrl, flag.path, { id_condition: cod, [flag.body]: next });
      api?.setCondicoesList?.((prev) => prev.map((r) => (
        r.TB01055_CODIGO === cod ? { ...r, [flag.state]: next ? 1 : 0 } : (next && flag.other ? { ...r, [flag.state]: 0 } : r)
      )));
    } catch (e) {
      setError(e.message || 'Erro ao atualizar');
    }
  };

  const handleSave = async () => {
    if (saving) return;
    if (nativeMode && !form.name.trim()) {
      setError('Informe o nome da condição');
      return;
    }
    if (!isEdit && erpTransition && !form.erpCode.trim()) {
      setError('Enquanto houver OS do ERP em aberto nesta transição, informe um código que já exista no ERP.');
      setTab('geral');
      return;
    }
    if (!isEdit && form.erpCode.trim()) {
      const erpMsg = await checkErpCode(baseUrl, form.erpCode, (code) => setForm((p) => ({ ...p, erpCode: code })));
      if (erpMsg) {
        setErpError(erpMsg);
        setTab('geral');
        return;
      }
    }
    const partial = GRUPO.some((k) => {
      const len = CAMPOS_CONFIG.find((c) => c.key === k)?.maxLen ?? 2;
      const v = form[k];
      return v && !isExactAlnumCode(v, len);
    });
    if (partial || (form.status_retorno && !isExactAlnumCode(form.status_retorno, 2)) || (form.status_peca_disp && !isExactAlnumCode(form.status_peca_disp, 2))) {
      setError('Cada código precisa estar completo ou vazio.');
      return;
    }
    if (form.status_peca_disp && !grupoReady(form)) {
      setError('Preencha operação, condição e os dois status antes do status de peça.');
      return;
    }
    if (!isEdit && form.flags.func_sem_pendencia) {
      const draft = { ...tipoRow, func_sem_pendencia: 1 };
      const block = formatSemPendenciaTipoOsBlockMessage(getSemPendenciaTipoOsIssues(draft), { forEnable: true });
      if (block) {
        setError(block);
        return;
      }
      if (erpTransition && !form.erpCode.trim()) {
        setError('Enquanto houver OS do ERP em aberto nesta transição, "Sem pendência" só pode ficar numa condição com código do ERP.');
        return;
      }
    }
    setSaving(true);
    setError(null);
    let cod = createdCode || codigo;
    try {
      if (nativeMode && !isEdit && !createdCode) {
        const res = await fetch(`${baseUrl}/api/config/condicoes-intervencao/native/conditions`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ name: form.name.trim(), erpCode: form.erpCode.trim() }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.message || 'Não foi possível cadastrar');
        cod = json.data?.codigo;
        setCreatedCode(cod);
      } else if (nativeMode && isEdit) {
        const res = await fetch(`${baseUrl}/api/config/condicoes-intervencao/native/conditions/${encodeURIComponent(codigo)}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ name: form.name.trim(), isActive: form.isActive }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.message || 'Não foi possível salvar');
      }
      const canBind = cod && (enabled || createdCode || (!isEdit && cod));
      if (canBind) await persistCampos(baseUrl, cod, form, isEdit ? row : null);
      if (!isEdit && cod) {
        for (const field of COND_TIPO_OS_FIELDS) {
          if (!form.tipos[field.db]) continue;
          await patchJson(baseUrl, 'tipo-os', { id_condition: cod, field: field.db, value: 1 });
        }
        for (const flag of FLAGS) {
          if (!form.flags[flag.state]) continue;
          if (flag.other && api?.[flag.other]?.(cod)) throw new Error(flag.otherMsg);
          await patchJson(baseUrl, flag.path, { id_condition: cod, [flag.body]: true });
        }
      }
      if (canBind && api?.defeitoColInteractive && defeitoDirty(form, isEdit ? row : null)) {
        await patchJson(baseUrl, 'codbug', {
          id_condition: cod,
          codBug: form.codbug.trim() || null,
          codbugReason: form.codbugReason.trim() || null,
          codbugBlank: !!form.codbugBlank,
          ...(nativeMode ? { askServiceOnNewCycle: !!form.askServiceOnNewCycle } : {}),
        });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (cod && !isEdit) onSaved?.();
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const title = isEdit ? (form.name || row?.TB01055_NOME || 'Condição') : 'Nova condição';
  const codeLabel = isEdit ? (form.erpCode || row?.TB01055_CODIGO || '') : (form.erpCode || 'Sem código');
  const selectedTipos = COND_TIPO_OS_FIELDS.filter((f) => normCondOsFlag(tipoRow[f.db]) === 1).map((f) => f.db);
  const flagOn = (flag) => (isEdit && row ? onFlag(row, flag.state) : !!form.flags[flag.state]);
  const slaReady = isEdit
    ? Boolean(api?.rowPodeUsarAtualizaSla?.(row))
    : grupoReady(form) && isExactAlnumCode(form.status_retorno, 2) && isExactAlnumCode(form.status_peca_disp, 2);

  return ReactDOM.createPortal(
    <form className="tm-overlay tm-cond" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose?.(); }} onSubmit={(e) => e.preventDefault()}>
      <div className="tm-modal" role="dialog" aria-modal="true" aria-labelledby="tm-cond-title">
        <aside className="tm-sidebar">
          <div className="tm-profile">
            <div className="tm-avatar is-empty" aria-hidden><Tags size={22} strokeWidth={1.75} /></div>
            <div className="tm-profile-text">
              {isEdit ? <span className="tm-eyebrow">Editar condição</span> : null}
              <h2 id="tm-cond-title" className="tm-profile-name" title={title}>{title}</h2>
              <p className="tm-profile-role">{isEdit ? 'Condição de intervenção' : 'Será criada habilitada para fechar OS'}</p>
            </div>
            <div className="tm-profile-chips">
              <span className="tm-chip"><Hash size={12} strokeWidth={1.75} /> {codeLabel}</span>
            </div>
          </div>
          <nav className="tm-nav tm-cond-nav" role="tablist" aria-label="Seções da condição">
            {TABS.map((item) => {
              const NavIcon = item.Icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.id}
                  className={`tm-nav-item${tab === item.id ? ' is-active' : ''}`}
                  onClick={() => setTab(item.id)}
                >
                  <NavIcon className="tm-icon" size={16} strokeWidth={1.75} />
                  <span className="tm-nav-label">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>
        <div className="tm-main">
          <header className="tm-main-head">
            <div>
              <h2 className="tm-main-title">{TABS.find((item) => item.id === tab)?.label}</h2>
              <p className="tm-main-desc">{TABS.find((item) => item.id === tab)?.desc}</p>
            </div>
            <button type="button" className="tm-icon-btn" onClick={onClose} aria-label="Fechar"><X size={18} strokeWidth={1.75} /></button>
          </header>
          <div className="tm-body">
            {loading ? <p className="eq2-tdp-muted">Carregando…</p> : (
              <div className="tm-stack tm-fade">
                {tab === 'geral' ? <>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Identificação</h3>
                    <p className="tm-section-desc">Nome e código usado no fechamento.</p>
                  </header>
                  <div className="tm-section-body">
                    <div className="tm-grid">
                      <label className="tm-field tm-col-2">
                        <span className="tm-label">Nome {nativeMode ? <span className="tm-required">*</span> : null}</span>
                        <input className="tm-control" value={nativeMode ? form.name : (row?.TB01055_NOME || '')} maxLength={60} disabled={!nativeMode} autoFocus={nativeMode} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                      </label>
                      <label className="tm-field">
                        <span className="tm-label">{!isEdit && erpTransition ? 'Código no ERP *' : 'Código no ERP'}</span>
                        <input
                          className={`tm-control${erpError ? ' is-invalid' : ''}`}
                          value={isEdit ? (form.erpCode || '—') : form.erpCode}
                          disabled={isEdit}
                          placeholder={isEdit ? '' : 'Ex.: 0001'}
                          onChange={(e) => {
                            setErpError(null);
                            setForm((p) => ({ ...p, erpCode: e.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 4) }));
                          }}
                          onBlur={() => {
                            if (isEdit || !form.erpCode.trim()) return;
                            checkErpCode(baseUrl, form.erpCode, (code) => setForm((p) => ({ ...p, erpCode: code }))).then(setErpError);
                          }}
                        />
                        {erpError ? <span className="tm-field-error">{erpError}</span> : null}
                      </label>
                    </div>
                    {isEdit && nativeMode ? (
                      <label className={`tm-toggle-card${form.isActive ? ' is-on' : ''}`} style={{ marginTop: 14 }}>
                        <span className="tm-toggle-text"><span className="tm-toggle-title">Ativa</span></span>
                        <TmSwitch checked={form.isActive} onChange={(v) => setForm((p) => ({ ...p, isActive: v }))} />
                      </label>
                    ) : null}
                    {!isEdit ? (
                      <p className="tm-hint" style={{ marginTop: 10 }}>
                        {erpTransition
                          ? 'Enquanto houver OS do ERP em aberto, o código do ERP é obrigatório e precisa já existir nesse cadastro.'
                          : 'Sem código no ERP, a condição não fecha OS do ERP.'}
                      </p>
                    ) : null}
                  </div>
                </section>

                <section className="tm-section">
                  <header className="tm-section-head">
                    <div className="tm-section-title-row">
                      <h3 className="tm-section-title">Tipos de OS</h3>
                      <HelpMark id="tipo-os" text={TOOLTIP_TIPO_OS} helpKey={helpKey} setHelpKey={setHelpKey} />
                    </div>
                    <p className="tm-section-desc">Os mesmos tipos da coluna. A condição só aparece nesses fechamentos.</p>
                  </header>
                  <div className="tm-section-body">
                    {enabled ? (
                      <div className="user-form-toggle-wrap" role="group" aria-label="Tipos de ordem de serviço">
                        {COND_TIPO_OS_FIELDS.map((item) => {
                          const active = selectedTipos.includes(item.db);
                          const Icon = TIPO_ICONS[item.db];
                          return (
                            <button key={item.db} type="button" aria-pressed={active} className={`user-form-toggle-pill${active ? ' active' : ''}`} onClick={() => toggleTipo(item)}>
                              <Icon size={14} strokeWidth={2} aria-hidden />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : <p className="tm-hint">Habilite a condição para escolher os tipos de OS.</p>}
                  </div>
                </section>

                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Funções</h3>
                    <p className="tm-section-desc">As marcações do fim da linha: SLA, instalação, contadores e sem pendência.</p>
                  </header>
                  <div className="tm-section-body">
                    {enabled ? FLAGS.map((flag) => {
                      const locked = flag.state === 'update_sla' && !slaReady;
                      return (
                        <label key={flag.state} className={`tm-toggle-card${flagOn(flag) ? ' is-on' : ''}`} style={{ marginTop: 8 }} title={locked ? 'Preencha operação, condição, status e peça antes de atualizar o SLA.' : undefined}>
                          <span className="tm-toggle-text">
                            <span className="tm-toggle-title tm-cond-label">
                              {flag.label}
                              {flag.help ? <HelpMark id={flag.state} text={flag.help} helpKey={helpKey} setHelpKey={setHelpKey} /> : null}
                            </span>
                          </span>
                          <TmSwitch checked={flagOn(flag)} disabled={locked} onChange={(v) => { if (!locked) toggleFlag(flag, v); }} />
                        </label>
                      );
                    }) : <p className="tm-hint">Habilite a condição para usar estas marcações.</p>}
                  </div>
                </section>
                </> : null}

                {tab === 'pedido' ? (
                <>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Pedido</h3>
                    <p className="tm-section-desc">Os mesmos códigos da coluna de operação, condição e status.</p>
                  </header>
                  <div className="tm-section-body">
                    {!enabled ? <p className="tm-hint">Habilite a condição para gravar estes códigos.</p> : null}
                    <div className="tm-grid">
                      {CAMPOS_CONFIG.filter((campo) => PEDIDO_KEYS.includes(campo.key)).map((campo) => (
                        <label className="tm-field" key={campo.key}>
                          <span className="tm-label">{campo.label}</span>
                          <input className="tm-control" value={form[campo.key] || ''} maxLength={campo.maxLen} disabled={!enabled} onChange={setCampo(campo.key, campo.maxLen)} spellCheck={false} autoComplete="off" />
                        </label>
                      ))}
                    </div>
                  </div>
                </section>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Validação de estoque</h3>
                    <p className="tm-section-desc">Escolha se a conferência olha todas as empresas ou só a empresa da OS.</p>
                  </header>
                  <div className="tm-section-body">
                    <div className="ci2-grupo-stock-options tm-cond-stock" role="radiogroup" aria-label="Validação de estoque">
                      {STOCK_VALIDATION_OPTIONS.map((opt) => (
                        <div key={opt.value} className="ci2-grupo-stock-option-wrap">
                          <div className={`ci2-grupo-stock-option-head${helpKey === opt.value ? ' is-help-open' : ''}`}>
                            <label className="dv2-sev-radio-item ci2-grupo-stock-option">
                              <input
                                type="radio"
                                className="dv2-sev-radio-input"
                                name="tm-cond-stock"
                                value={opt.value}
                                checked={normalizeStockValidationScope(form.stock_validation_scope) === opt.value}
                                disabled={!enabled}
                                onChange={() => setForm((p) => ({ ...p, stock_validation_scope: opt.value }))}
                              />
                              <span className="dv2-sev-radio-mark" aria-hidden />
                              <span className="dv2-sev-radio-text">{opt.label}</span>
                            </label>
                            <HelpMark id={opt.value} text={opt.tip} helpKey={helpKey} setHelpKey={setHelpKey} className="ci2-grupo-stock-help" tipClass="ci2-grupo-stock-tip" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
                </>
                ) : null}

                {tab === 'retorno' ? (
                <>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Retorno</h3>
                    <p className="tm-section-desc">Código de status usado na ordem de retorno.</p>
                  </header>
                  <div className="tm-section-body">
                    {!enabled ? <p className="tm-hint">Habilite a condição para gravar o retorno.</p> : null}
                    <label className="tm-field">
                      <span className="tm-label tm-cond-label">
                        Status de retorno
                        <HelpMark id="status_retorno" text={RETORNO_HELP} helpKey={helpKey} setHelpKey={setHelpKey} />
                      </span>
                      <input className="tm-control" value={form.status_retorno || ''} maxLength={2} disabled={!enabled} onChange={setCampo('status_retorno', 2)} spellCheck={false} autoComplete="off" />
                    </label>
                  </div>
                </section>
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Serviço</h3>
                    <p className="tm-section-desc">Serviço e motivo que a OS de retorno já abre preenchidos.</p>
                  </header>
                  <div className="tm-section-body">
                    {api?.defeitoColInteractive ? (
                      <>
                        <label className="tm-field">
                          <span className="tm-label tm-cond-label">
                            Serviço
                            <HelpMark id="servico" text={SERVICO_HELPS.servico} helpKey={helpKey} setHelpKey={setHelpKey} />
                          </span>
                          <ServicoPicker
                            value={form.codbug}
                            items={api.regBugsList || []}
                            disabled={!enabled}
                            onNeedItems={api.loadRegBugsIfNeeded}
                            onChange={(cod) => setForm((p) => ({ ...p, codbug: cod || '' }))}
                          />
                        </label>
                        <label className="tm-field" style={{ marginTop: 12 }}>
                          <span className="tm-label tm-cond-label">
                            Motivo
                            <HelpMark id="motivo" text={SERVICO_HELPS.motivo} helpKey={helpKey} setHelpKey={setHelpKey} />
                          </span>
                          <input
                            className="tm-control"
                            value={form.codbugReason}
                            maxLength={50}
                            disabled={!enabled}
                            placeholder="Opcional"
                            spellCheck={false}
                            autoComplete="off"
                            onChange={(e) => setForm((p) => ({ ...p, codbugReason: sanitizeMotivoDraft(e.target.value) }))}
                          />
                        </label>
                        <div className="tm-cond-blank">
                          <button
                            type="button"
                            className={`user-form-type-chip${form.codbugBlank ? ' is-on' : ''}`}
                            disabled={!enabled}
                            aria-pressed={!!form.codbugBlank}
                            onClick={() => setForm((p) => ({ ...p, codbugBlank: !p.codbugBlank }))}
                          >
                            <span className={`user-form-type-chip-mark${form.codbugBlank ? ' is-on' : ''}`} aria-hidden />
                            Abrir sem defeito
                          </button>
                          <HelpMark id="blank" text={SERVICO_HELPS.blank} helpKey={helpKey} setHelpKey={setHelpKey} />
                        </div>
                        {nativeMode ? (
                          <div className="tm-cond-blank">
                            <button
                              type="button"
                              className={`user-form-type-chip${form.askServiceOnNewCycle ? ' is-on' : ''}`}
                              disabled={!enabled}
                              aria-pressed={!!form.askServiceOnNewCycle}
                              onClick={() => setForm((p) => ({ ...p, askServiceOnNewCycle: !p.askServiceOnNewCycle }))}
                            >
                              <span className={`user-form-type-chip-mark${form.askServiceOnNewCycle ? ' is-on' : ''}`} aria-hidden />
                              Perguntar novo defeito em novo ciclo
                            </button>
                            <HelpMark id="ask" text={SERVICO_HELPS.ask} helpKey={helpKey} setHelpKey={setHelpKey} />
                          </div>
                        ) : null}
                      </>
                    ) : <input className="tm-control" value="—" disabled title="Serviço no retorno técnico está desabilitado nas Configurações." />}
                  </div>
                </section>
                </>
                ) : null}

                {tab === 'estoque' ? (
                <section className="tm-section">
                  <header className="tm-section-head">
                    <h3 className="tm-section-title">Liberação</h3>
                    <p className="tm-section-desc">Status do pedido de peça no momento em que a saída de estoque está liberada.</p>
                  </header>
                  <div className="tm-section-body">
                    {!enabled ? <p className="tm-hint">Habilite a condição para gravar este status.</p> : null}
                    <div className="tm-grid">
                      <label className="tm-field">
                        <span className="tm-label tm-cond-label">
                          Status peça disponível
                          <HelpMark id="status_peca_disp" text={CAMPOS_CONFIG.find((c) => c.key === 'status_peca_disp')?.tooltip} helpKey={helpKey} setHelpKey={setHelpKey} />
                        </span>
                        <input className="tm-control" value={form.status_peca_disp || ''} maxLength={2} disabled={!enabled} onChange={setCampo('status_peca_disp', 2)} spellCheck={false} autoComplete="off" />
                      </label>
                    </div>
                  </div>
                </section>
                ) : null}
              </div>
            )}
            {error ? <p className="tm-field-error" role="alert">{error}</p> : null}
          </div>
          <footer className="tm-footer">
            <span className={`tm-footer-status${error ? ' is-danger' : ''}`}>{error || null}</span>
            <div className="tm-footer-actions">
              <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onClose} disabled={saving}>Cancelar</button>
              <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={handleSave} disabled={saving || loading}>
                {saving ? 'Salvando…' : isEdit ? 'Salvar' : 'Cadastrar'}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </form>,
    document.body,
  );
}

function HelpMark({ id, text, helpKey, setHelpKey, className = '', tipClass = 'ci2-th-help-popover' }) {
  const open = helpKey === id;
  return (
    <span className="tm-cond-help">
      <Ci2HelpHexIcon
        className={className}
        label="Mais informações"
        expanded={open}
        onToggle={() => setHelpKey(open ? null : id)}
      />
      {open ? <div className={tipClass} role="tooltip">{text}</div> : null}
    </span>
  );
}

async function checkErpCode(baseUrl, raw, onCanonical) {
  const res = await fetch(`${baseUrl}/api/config/condicoes-intervencao/native/erp-code/${encodeURIComponent(raw.trim())}`, { headers: authHeaders() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) return json.message || 'Não foi possível validar o código do ERP.';
  if (json.data?.erpCode) onCanonical?.(json.data.erpCode);
  if (!json.data?.exists) return 'Código não encontrado no cadastro de condições do ERP';
  if (json.data?.taken) return 'Este código do ERP já pertence a uma condição cadastrada. Use a condição existente.';
  return null;
}

function formFromRow(row) {
  return {
    ...EMPTY,
    name: row?.TB01055_NOME || '',
    erpCode: row?.erp_code || (/^N\d+$/i.test(String(row?.TB01055_CODIGO || '')) ? '' : (row?.TB01055_CODIGO || '')),
    operacao: String(row?.operacao ?? '').trim(),
    condicao: String(row?.condicao ?? '').trim(),
    status_disponivel: String(row?.status_disponivel ?? '').trim(),
    status_indisponivel: String(row?.status_indisponivel ?? '').trim(),
    status_retorno: String(row?.status_retorno ?? '').trim(),
    status_peca_disp: String(row?.status_peca_disp ?? '').trim(),
    stock_validation_scope: normalizeStockValidationScope(row?.stock_validation_scope),
    codbug: row?.codbug || '',
    codbugReason: row?.codbug_reason || '',
    codbugBlank: row?.codbug_blank === 1 || row?.codbug_blank === true,
    askServiceOnNewCycle: row?.ask_service_on_new_cycle === 1 || row?.ask_service_on_new_cycle === true,
  };
}

function grupoReady(form) {
  return GRUPO.every((k) => {
    const len = CAMPOS_CONFIG.find((c) => c.key === k)?.maxLen ?? 2;
    return isExactAlnumCode(form[k], len);
  });
}

function defeitoDirty(form, previous) {
  const cod = String(form.codbug || '').trim();
  const reason = String(form.codbugReason || '').trim();
  const blank = !!form.codbugBlank;
  const ask = !!form.askServiceOnNewCycle;
  if (!previous) return Boolean(cod || reason || blank || ask);
  const prevBlank = previous.codbug_blank === 1 || previous.codbug_blank === true;
  const prevAsk = previous.ask_service_on_new_cycle === 1 || previous.ask_service_on_new_cycle === true;
  return cod !== String(previous.codbug || '').trim()
    || reason !== String(previous.codbug_reason || '').trim()
    || blank !== prevBlank
    || ask !== prevAsk;
}

async function persistCampos(baseUrl, cod, form, previous) {
  const same = (key) => previous && String(form[key] || '').trim() === String(previous[key] || '').trim();
  const stockSame = previous && normalizeStockValidationScope(form.stock_validation_scope) === normalizeStockValidationScope(previous.stock_validation_scope);
  if (grupoReady(form)) {
    const dirty = !previous || GRUPO.some((k) => !same(k)) || !same('status_retorno') || !same('status_peca_disp') || !stockSame;
    if (!dirty) return;
    await patchJson(baseUrl, 'campos', {
      id_condition: cod,
      operacao: form.operacao,
      condicao: form.condicao,
      statusDisponivel: form.status_disponivel,
      statusIndisponivel: form.status_indisponivel,
      statusRetorno: form.status_retorno,
      statusPecaDisp: form.status_peca_disp,
      stockValidationScope: normalizeStockValidationScope(form.stock_validation_scope),
    });
    return;
  }
  if (!same('status_retorno') && (form.status_retorno || previous?.status_retorno)) {
    await patchJson(baseUrl, 'campos', {
      id_condition: cod,
      statusRetorno: form.status_retorno,
      singleField: 'retorno',
    });
  }
}

export default CondicoesIntervNativeConditionModal;
